import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  THEME_STORAGE_KEY,
  applyThemeToDocument,
  readStoredPreference,
  resolveTheme,
} from './resolveTheme.js'
import { ThemeContext } from './theme-context.js'

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() =>
    typeof window !== 'undefined' ? readStoredPreference() : 'light',
  )

  const resolvedTheme = useMemo(() => resolveTheme(preference), [preference])

  const setPreference = useCallback((next) => {
    setPreferenceState(next)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
    applyThemeToDocument(resolveTheme(next))
  }, [])

  useEffect(() => {
    applyThemeToDocument(resolvedTheme)
  }, [resolvedTheme])

  const cycleTheme = useCallback(() => {
    setPreference(preference === 'light' ? 'dark' : 'light')
  }, [preference, setPreference])

  const value = useMemo(
    () => ({
      preference,
      resolvedTheme,
      setPreference,
      cycleTheme,
    }),
    [preference, resolvedTheme, setPreference, cycleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
